import { NavLink, useParams } from "react-router";
import { isImmutable } from "@/services/backend/documents/lib/document-state";
import {
  useHtzConfirmationShowQuery,
  useHtzConfirmationUpdateStateMutation,
} from "@/services/backend/htz/confirmations/api/confirmation-api";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import t from "@/lang/lang";
import { PersistenceContextProvider } from "@/shared/lib/persistence-state/provider";
import { PersistenceStateUsingContext } from "@/shared/lib/persistence-state/persistence-state-info";
import { ConfirmationPdf } from "@/routes/processes/[processId]/htz/confirmations/[confirmationId]/_components/confirmation-pdf";
import { DeleteConfirmationDialog } from "@/routes/processes/[processId]/htz/confirmations/[confirmationId]/_components/delete-confirmation-dialog";
import { ErrValidationFailedContextProvider } from "@/shared/lib/errors/err-validation-failed-context";
import {
  DocumentPage,
  DocumentPageActions,
  DocumentPageContent,
  DocumentPageHeader,
  DocumentPageNavigation,
  DocumentPageNavigationLink,
  DocumentPageNavigationOutlet,
  DocumentPageSkeleton,
} from "@/shared/bsm/core/document/components/ui/document-page";
import { DocumentStateAlert } from "@/shared/bsm/core/document/components/ui/document-state-alert";
import { DocumentStateDropdown } from "@/shared/bsm/core/document/components/ui/document-state-dropdown";
import { CpyDialog } from "@/services/backend/cpy/components/cpy-dialog";
import { CpyName } from "@/services/backend/cpy/types/entity-names";
import { NullUUID } from "@bitory-gmbh/nidavellir";
import { Confirmation } from "@/services/backend/htz/confirmations/types/confirmation";
import {
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/shared/components/ui/breadcrumb";
import { ProcessPageMainTemplate } from "@/routes/processes/[processId]/_components/process-page-main-template";
import { HtzBreadcrumbs } from "@/routes/processes/[processId]/htz/_components/htz-breadcrumbs";
import { ReferenceNumber } from "@/services/backend/documents/components/reference-number";

export function ConfirmationRoute() {
  const { confirmationId } = useParams();

  const {
    data: confirmation,
    isLoading,
    error,
  } = useHtzConfirmationShowQuery({ id: confirmationId! });

  const breadcrumbs = confirmation && (
    <HtzBreadcrumbs
      customerId={confirmation.customerId}
      processId={confirmation.processId!}
    >
      <BreadcrumbSeparator />
      <BreadcrumbItem className="hidden md:block">
        <BreadcrumbLink asChild>
          <NavLink
            to={`/processes/htz/${confirmation.processId!}/confirmations`}
          >
            {t("Auftragsbestätigungen")}
          </NavLink>
        </BreadcrumbLink>
      </BreadcrumbItem>
      <BreadcrumbSeparator />
      <BreadcrumbItem>
        <BreadcrumbPage>
          <ReferenceNumber referenceNumber={confirmation.referenceNumber} />
        </BreadcrumbPage>
      </BreadcrumbItem>
    </HtzBreadcrumbs>
  );

  return (
    <ProcessPageMainTemplate breadcrumbs={breadcrumbs}>
      {error && <RTKQueryErrorAlert error={error} />}
      {isLoading && <DocumentPageSkeleton />}
      {confirmation && (
        <PersistenceContextProvider>
          <ErrValidationFailedContextProvider>
            <ConfirmationPage confirmation={confirmation} />
          </ErrValidationFailedContextProvider>
        </PersistenceContextProvider>
      )}
    </ProcessPageMainTemplate>
  );
}

function ConfirmationPage({ confirmation }: { confirmation: Confirmation }) {
  const links = [
    {
      label: t("Auftragsbestätugung"),
      to: "data",
      fieldName: [
        "referenceNumber",
        "date",
        "orderId",
        "orderReference",
        "orderDate",
        "offerId",
        "offerReference",
        "offerDate",
        "specialAgreements",
        "internalComment",
      ],
    },
    {
      label: t("Leistungen"),
      to: "positions",
      fieldName: ["positions"],
    },
    {
      label: t("Adressen & Kontakte"),
      to: "addresses-and-contacts",
      fieldName: [
        "postalAddress",
        "serviceAddress",
        "serviceContact",
        "representative",
      ],
    },
  ];

  return (
    <DocumentPage>
      <DocumentStateAlert document={confirmation} />
      <DocumentPageHeader>
        <DocumentPageNavigation>
          {links.map((link) => (
            <DocumentPageNavigationLink
              key={link.to}
              to={link.to}
              fieldName={link.fieldName}
            >
              {link.label}
            </DocumentPageNavigationLink>
          ))}
        </DocumentPageNavigation>
        <DocumentPageActions>
          {!isImmutable(confirmation) && (
            <>
              <PersistenceStateUsingContext className="mr-2" />
              <CpyDialog
                destinationEntityName={CpyName.HTZConfirmation}
                destinationId={confirmation.id}
                initialParams={{
                  // We limit the results to documents
                  // from the same process. This
                  // is the 90% default what users
                  // need. If needed, they can change
                  // the filter.
                  process: {
                    active: confirmation.processId !== null,
                    values: [confirmation.processId || NullUUID],
                  },
                  orderBy: [{ field: "updatedAt", dsc: true }],
                }}
              />
              <DeleteConfirmationDialog confirmationId={confirmation.id} />
            </>
          )}
          <DocumentStateDropdown
            document={confirmation}
            mutation={useHtzConfirmationUpdateStateMutation}
          />
          <ConfirmationPdf confirmation={confirmation} />
        </DocumentPageActions>
      </DocumentPageHeader>
      <DocumentPageContent>
        <DocumentPageNavigationOutlet />
      </DocumentPageContent>
    </DocumentPage>
  );
}
